/**
 * Glossary registry — single source of truth for /learn, /learn/<slug>, the
 * sitemap, and the term strips on template pages. Add a term here and it
 * lands everywhere; nothing else needs wiring.
 *
 * Hard rule for this file: every "In FrazzleCRM" bullet must describe
 * something the product ACTUALLY does. The definitions are the part AI
 * answer engines quote, so an invented capability here becomes a lie with a
 * citation attached. When a term names an industry practice FrazzleCRM has
 * no first-class object for (lead scoring, double opt-in), say so plainly
 * and show how it is assembled from the primitives that do exist — that is
 * more useful than a fake feature bullet anyway.
 *
 * `templates` entries must be real slugs from src/templates; `related` must
 * be real slugs from this file. A typo in either is a build failure, not a
 * dead link at runtime: /learn/[slug]'s getStaticProps resolves both lists
 * and throws on an unknown slug, so `next build` catches it.
 */

export type GlossaryCategory =
  | "Automation mechanics"
  | "Contacts & data"
  | "Email & SMS delivery"
  | "Funnel strategy";

export type GlossaryTerm = {
  /** URL slug: /learn/<slug> */
  slug: string;
  term: string;
  /** Other names people search for. Rendered, and used in the hub filter. */
  aka?: string[];
  category: GlossaryCategory;
  /**
   * One or two sentences that stand alone out of context — this is the
   * string that gets quoted in an AI answer or a featured snippet.
   */
  definition: string;
  /** The substance. Each string is a paragraph. */
  body: string[];
  /** Concrete, verifiable mechanics. */
  inFrazzle: string[];
  /** The mistake people actually make. Optional but usually the best part. */
  gotcha?: string;
  faq: { q: string; a: string }[];
  /**
   * An optional pointer to the marketing page that covers this term as a
   * product feature — rendered under the "How it works" box. Terms without
   * a dedicated page simply omit it.
   */
  deepDive?: { href: string; label: string; blurb: string };
  /** Template slugs that demonstrate the term. */
  templates: string[];
  /** Other glossary slugs. */
  related: string[];
};

export const GLOSSARY: GlossaryTerm[] = [
  // ---------------------------------------------------------------- strategy
  {
    slug: "funnel-map",
    term: "Funnel map",
    aka: ["funnel diagram", "customer journey map", "marketing funnel chart"],
    category: "Funnel strategy",
    definition:
      "A funnel map is a diagram of every step a lead takes from first touch to customer — traffic sources, pages, messages, waits, branches and outcomes — laid out as a single picture instead of a list of disconnected campaigns.",
    body: [
      "Most teams have a funnel map somewhere: a Miro board, a Figma file, a whiteboard photo in Slack. It is drawn once during planning, diverges from the real automations within a fortnight, and from then on it is a picture of what someone once intended rather than of what is running.",
      "The map is worth keeping because it answers questions a campaign list cannot. Where do leads leave? Which email is doing the work? What happens to somebody who books but does not show? Those are questions about the shape of the journey, and shapes are easier to read than a table of workflow names.",
      "The distinction that matters when choosing a tool is whether the diagram is documentation or infrastructure. A documentation diagram needs a human to keep it honest. An executable one cannot drift, because editing it is the only way to change what runs.",
    ],
    inFrazzle: [
      "The map is the automation. Every automation appears on the canvas as a Flow, with a trigger node and one node per step; drop a Send Email node inside a bound flow and a real email step exists in the automation editor.",
      "Sync runs both ways: edits made in the automation editor reshape the map, and edits on the map reshape the automation.",
      "Nodes that are deliberately illustrative — a competitor's tool, a channel you have not built yet — get marked Map only, drawn with hatching and a dashed border so a reader can tell diagram from runtime at a glance.",
      "Maps can be shared as read-only public links or embedded in someone else's page as a live iframe.",
    ],
    gotcha:
      "A map that is only a picture is a liability, not an asset — it gets trusted precisely because it is legible, and it is wrong. If your diagram lives in a separate tool from your automations, put a review date on it or accept that it is decoration.",
    faq: [
      {
        q: "What is the difference between a funnel map and a workflow builder?",
        a: "A workflow builder shows one automation at a time. A funnel map shows the whole journey — every automation, plus the pages, traffic sources and outcomes between them — so you can see where one flow hands off to another.",
      },
      {
        q: "Do I need to map a funnel before building it?",
        a: "Not necessarily, but mapping first catches the gaps that are expensive later: the branch with nothing on the other side, the reminder that fires after the appointment, the sequence with no exit.",
      },
    ],
    templates: ["lead-magnet-nurture", "webinar-funnel", "saas-trial-onboarding"],
    related: ["automation-trigger", "nurture-sequence", "goal"],
  },
  {
    slug: "lead-magnet",
    term: "Lead magnet",
    aka: ["opt-in offer", "content upgrade", "freebie"],
    category: "Funnel strategy",
    definition:
      "A lead magnet is something useful given away in exchange for contact details — a checklist, template, calculator, sample or short course — used to convert anonymous traffic into a named contact you can follow up with.",
    body: [
      "The mechanics are simple: an opt-in page offers the thing, a form captures an email address, the thing gets delivered, and a follow-up sequence starts. Almost nobody gets the mechanics wrong. What they get wrong is the offer.",
      "A lead magnet works when it solves one specific problem the buyer already knows they have, and when solving it makes your paid offer the obvious next step. A generic ebook attracts generic subscribers. A pricing calculator for the exact job you sell attracts people who are pricing that job.",
      "Judge it by what happens after delivery, not by opt-in rate. A magnet with a 60% opt-in rate that produces no calls is worse than one at 18% that produces five, and only the downstream numbers can tell you which you have.",
    ],
    inFrazzle: [
      "Draw the opt-in page and the delivery email on the map; the delivery email becomes a real Send Email step the moment it sits inside a bound flow.",
      "Entry can be a form submission, an inbound webhook posted by whatever hosts your page, or a tag added by an import.",
      "The delivery email carries the download as a normal link; wrapping it as a trigger link additionally records who clicked and can start a follow-up flow.",
      "The Lead Magnet + Nurture template ships the whole shape — capture, deliver, nurture, hand off — as an editable map.",
    ],
    gotcha:
      "Delivering the magnet and starting the nurture from the same step means a delivery failure silently costs you the sequence too. Deliver first, then enrol — and keep the download link out of the sequence's stop-tag path so unsubscribing never revokes the thing you promised.",
    faq: [
      {
        q: "Should the lead magnet be gated behind double opt-in?",
        a: "Only if your jurisdiction or your sending reputation requires it. Confirmation adds a step that costs real signups; it earns its place when list quality matters more than list size.",
      },
      {
        q: "How many lead magnets should one business have?",
        a: "One per problem you solve, not one per format. Five magnets addressing the same pain fragment your list; two addressing distinct pains let you segment on entry.",
      },
    ],
    templates: ["lead-magnet-nurture", "quiz-funnel-segmentation", "newsletter-welcome-series"],
    related: ["nurture-sequence", "tag", "trigger-link"],
  },
  {
    slug: "nurture-sequence",
    term: "Nurture sequence",
    aka: ["nurture campaign", "follow-up sequence", "email sequence"],
    category: "Funnel strategy",
    definition:
      "A nurture sequence is a scheduled series of emails or texts sent to a lead who is not ready to buy, designed to keep the relationship alive and move them toward a specific next action — usually a call, a demo or a first purchase.",
    body: [
      "The word nurture hides a decision: what is the sequence for? A sequence with no target action is a newsletter with extra steps. A sequence aimed at one action — book the call, start the trial, reply with your timeline — can be judged, shortened and fixed.",
      "Good sequences are shaped by the sales cycle, not by a habit. A £40 impulse product needs three messages over four days. A £40,000 service with a nine-month cycle needs something that can run for months without exhausting either the reader or your goodwill.",
      "The most valuable part of a sequence is its exit. Somebody who books the call should stop receiving messages asking them to book the call, immediately, on every channel. That single rule does more for reply quality than any amount of copy work.",
    ],
    inFrazzle: [
      "A sequence is an automation: alternating Send Email / Send SMS steps and Wait steps, drawn left to right on the map.",
      "Attach a Goal for the outcome — booked, replied, purchased — and contacts jump straight to it the moment the event happens, skipping everything in between.",
      "Set the automation's stop tags so an unsubscribe or a won deal cancels active runs rather than merely pausing new ones.",
      "Re-entry settings decide whether somebody can go through twice: once, after they exit, or repeatedly.",
    ],
    gotcha:
      "Sequences that stop only at the end still send the last three emails to a customer who bought on day two. If the outcome is not wired as a goal or a stop tag, the sequence does not know it succeeded.",
    faq: [
      {
        q: "How long should a nurture sequence be?",
        a: "As long as your sales cycle, not as long as the template you copied. Match the interval to how long a real buying decision takes in your market, and end it deliberately rather than letting it fade.",
      },
      {
        q: "Email or SMS?",
        a: "SMS gets read within minutes and burns goodwill fast; email tolerates length and repetition. Most working sequences are email with SMS reserved for time-critical moments — a reminder an hour before, a callback while the lead is still warm.",
      },
    ],
    templates: ["lead-magnet-nurture", "real-estate-buyer-nurture", "winback-reengagement"],
    related: ["drip-campaign", "goal", "stop-tag", "wait-step"],
  },
  {
    slug: "drip-campaign",
    term: "Drip campaign",
    aka: ["drip sequence", "autoresponder", "drip mode"],
    category: "Funnel strategy",
    definition:
      "A drip campaign delivers a pre-written series of messages on a fixed schedule after a contact enters it, so everyone receives the same messages in the same order relative to their own start date rather than on a shared calendar date.",
    body: [
      "Drip is the opposite of broadcast. A broadcast goes to a list at 10am on Tuesday; a drip goes to each person on their own day three, whenever their day three happens to fall. That relative timing is what makes onboarding, trials and post-purchase follow-up work at all.",
      "Confusingly, the same word also names a throughput control: releasing contacts into a flow in batches rather than all at once. A list import of 40,000 people hitting a sending domain in one minute is a deliverability incident; the same import released 200 at a time every ten minutes is a Tuesday.",
      "Both senses matter and they compose. The sequence is relative-timed; the entry into it is rate-limited.",
    ],
    inFrazzle: [
      "Relative timing is the default: a Wait step of 3 days means three days after that contact reached it, in the company timezone or the contact's own.",
      "The throughput sense is a real step type — Drip Mode, configured with a batch size and an interval. Contacts queue at it and are released in shared batches, one batch per interval.",
      "A send window on the automation queues email and SMS to the next open slot, so an overnight drip does not text anyone at 3am.",
      "Both are visible on the map as their own nodes, so nobody has to open settings to find out why 200 people moved and 39,800 did not.",
    ],
    gotcha:
      "Putting a Drip Mode step after the first email rather than before it defeats the point — the whole import still gets emailed at once, and only the second message is throttled.",
    faq: [
      {
        q: "What is the difference between a drip campaign and a newsletter?",
        a: "A drip is relative to each contact's entry date and is written once. A newsletter is absolute — everyone receives it at the same moment — and is written fresh each time.",
      },
      {
        q: "How fast should I drip a large import?",
        a: "Slow enough that your sending domain's reputation survives it. On a cold or newly warmed domain, batches in the low hundreds per hour are ordinary; established domains tolerate far more.",
      },
    ],
    templates: ["newsletter-welcome-series", "course-student-onboarding", "product-launch-runway"],
    related: ["nurture-sequence", "wait-step", "send-window", "spf-dkim-dmarc"],
  },
  {
    slug: "no-show",
    term: "No-show",
    aka: ["missed appointment", "no-show rate", "appointment attrition"],
    category: "Funnel strategy",
    definition:
      "A no-show is a booked appointment the contact never attends. No-show rate is the share of booked appointments lost this way, and for most service businesses it is the single largest recoverable leak between a lead and revenue.",
    body: [
      "No-shows are usually treated as a people problem and are almost always a timing problem. The gap between booking and appointment is where intent decays, and nothing in that gap reminds the person why they booked.",
      "Two mechanics move the number more than anything else: reminders timed relative to the appointment rather than to the booking, and a reply path that makes rescheduling easier than vanishing. Somebody who can text back and move the slot is not a no-show; they are next week's appointment.",
      "The third mechanic is what happens afterwards. A missed appointment with no follow-up is a dead lead. A missed appointment that triggers a same-day message with a rebooking link recovers a meaningful share of them.",
    ],
    inFrazzle: [
      "Appointment waits are relative to the appointment itself: a set number of minutes, hours or days before or after its start or end, so a 24-hour reminder stays 24 hours out even when the booking moves.",
      "An appointment-booked trigger can be scoped to one appointment type by title, so a discovery call and a delivery visit can run different reminder flows.",
      "Appointment-status triggers fire on booked, confirmed, showed, no-show and cancelled — the no-show branch is a real entry point, not something you have to detect yourself.",
      "Inbound replies are a trigger too, so a contact texting back can jump straight to a goal and stop the reminder sequence.",
    ],
    gotcha:
      "Reminders timed from the booking instead of the appointment fire at the wrong moment for anybody who reschedules — and the display default trap is real: if a wait's direction is left unset, check which way the tool actually reads it before trusting a reminder to arrive before the meeting.",
    faq: [
      {
        q: "How many reminders is too many?",
        a: "Two or three placed well beats five placed evenly: one at 24 hours, one at an hour, and — for high-value appointments — one at booking that confirms the details.",
      },
      {
        q: "Should reminders be SMS or email?",
        a: "SMS for the hour-before, because it is read in minutes. Email for the confirmation, because it holds the joining link, the address and the reschedule link without truncation.",
      },
    ],
    templates: ["appointment-reminders", "discovery-call-application", "estimate-to-job-won"],
    related: ["wait-step", "goal", "automation-trigger"],
  },

  // --------------------------------------------------- automation mechanics
  {
    slug: "automation-trigger",
    term: "Trigger",
    aka: ["automation trigger", "entry event", "workflow trigger"],
    category: "Automation mechanics",
    definition:
      "A trigger is the event that starts an automation for a contact — a tag being added, a form submitted, a link clicked, a date arriving — deciding both who enters the flow and when.",
    body: [
      "Everything downstream inherits the trigger's mistakes. Choose one that fires too broadly and your sequence reaches people it was never written for; choose one too narrow and the flow sits idle while you debug copy that nobody ever received.",
      "Triggers divide into events that happen to a contact (a tag added, a field changed, a reply arriving) and moments that arrive on their own (a date, a schedule). Date-driven triggers are the ones that surprise people: they run on a scan, so they fire on a cadence rather than the instant a value changes.",
      "An automation with no trigger at all is a legitimate and underused design. It runs only when something else enrols a contact — another flow, a webhook, a manual run — which is exactly what you want for a shared sub-sequence.",
    ],
    inFrazzle: [
      "An automation can carry several triggers at once, or none.",
      "Available triggers include contact created, tag added or removed, field changed, a date field with an offset (annually, for birthdays), a schedule with matching rules, appointment booked, appointment status changed, trigger link clicked, an inbound message from the contact, and — when company email tracking is on — email opened or clicked.",
      "Webhook entry is deliberately not a trigger type: external systems enrol contacts by posting to a wired Inbound Webhook URL instead.",
      "New automations start as drafts, and real triggers do not fire until the automation is set live.",
    ],
    gotcha:
      "A tag-added trigger on a flow that also adds that tag is an infinite loop, and it is the most common self-inflicted outage in any CRM. Frazzle's cross-automation audit looks for exactly this pattern, along with duplicate triggers and self-cancelling stop tags.",
    faq: [
      {
        q: "Can one automation have more than one trigger?",
        a: "Yes. Multiple triggers on one automation are ordinary — for example both a form submission and a tag added by an import feeding the same welcome sequence.",
      },
      {
        q: "Why did my date-based trigger fire late?",
        a: "Date field triggers run on a daily scan rather than watching the field continuously, so they fire on the scan following the date, at the time configured — not the moment the value changes.",
      },
    ],
    templates: ["speed-to-lead-callback", "birthday-loyalty-club", "failed-payment-dunning"],
    related: ["enrollment", "tag", "inbound-webhook", "re-entry"],
  },
  {
    slug: "enrollment",
    term: "Enrollment",
    aka: ["enrolment", "entering a flow", "workflow entry"],
    category: "Automation mechanics",
    definition:
      "Enrollment is a single contact's active run through one automation. Each enrollment holds its own position in the flow, its own timers and its own state, which is why two contacts in the same sequence can be at completely different steps.",
    body: [
      "The distinction between an automation and an enrollment explains most confusing behaviour in marketing automation. Editing a live automation does not rewind anybody. Contacts mid-flow keep their position, so a new step inserted at the top reaches only people who enter after the edit.",
      "It also explains duplicates. A contact with two active enrollments in the same sequence receives everything twice, and the cause is almost never the sending step — it is a trigger that fired twice with re-entry set to allow it.",
      "Because an enrollment carries state, it can be cancelled. Stopping a flow means ending the run, not deleting the automation, and a well-built sequence has at least one deliberate way for a run to end early.",
    ],
    inFrazzle: [
      "Runs are recorded per contact per automation, with statuses of active, completed, exited or failed — and those statuses are themselves usable as branch conditions elsewhere.",
      "Contacts enter through a trigger, an inbound webhook, an Add to Flow step in another automation, or a manual run.",
      "A Remove from Flow step cancels a contact's active runs in a named automation, or in the current one.",
      "Editing steps keeps existing step ids so contacts already mid-run are not disrupted; moving a live automation back to draft suspends its in-flight runs.",
    ],
    gotcha:
      "Adding a step to a sequence and then wondering why yesterday's leads never got it is not a bug. They enrolled before the step existed and are already past that point in the flow.",
    faq: [
      {
        q: "Can a contact be enrolled in the same automation twice?",
        a: "It depends on the re-entry setting — allowed repeatedly, once ever, or only after they have exited. Repeated enrollment is capped to protect against trigger loops.",
      },
      {
        q: "What happens to enrolled contacts if I pause an automation?",
        a: "Paused runs hold in place and re-check rather than dying, so pausing is a safe way to stop sending without losing everyone's position.",
      },
    ],
    templates: ["saas-trial-onboarding", "agency-client-onboarding", "churn-save-cancellation"],
    related: ["automation-trigger", "re-entry", "stop-tag", "goal"],
  },
  {
    slug: "wait-step",
    term: "Wait step",
    aka: ["delay step", "timer", "wait until"],
    category: "Automation mechanics",
    definition:
      "A wait step pauses a contact's run inside an automation until a condition is met — a duration elapsing, a clock time arriving, a date field coming due, an appointment approaching, or an event happening — then continues from where it left off.",
    body: [
      "Waits are what make an automation a sequence rather than a burst. They are also where the subtle bugs live, because a wait encodes an assumption about time and time is where assumptions go wrong: timezones, weekends, rescheduled meetings, dates that never arrive.",
      "The most useful distinction is between waiting for a duration and waiting for an event. A duration wait is a promise that the run resumes; an event wait may never resume, which is why every event wait needs a timeout and somewhere to go when it expires.",
      "Waiting until a date held on the contact is the pattern behind renewals, birthdays and expiry reminders — and the one that needs a fallback most, because the field is often empty on the contacts you care about.",
    ],
    inFrazzle: [
      "Wait modes: a duration, until a specific datetime, until a given day and time, relative to a date field on the contact, relative to an appointment, or until an event occurs.",
      "Duration waits accept an optional window, so a three-day wait can resume only on weekdays between 09:00 and 17:00.",
      "Date-field and datetime waits take a fallback — continue, exit, or jump to a named step — for contacts whose date is missing or already past.",
      "Event waits carry a timeout with its own target, and every wait can run in the company timezone or the contact's own.",
    ],
    gotcha:
      "An appointment wait whose direction is left unset is the classic silent failure: reminders fire at the appointment instead of before it. Whenever a control shows a default without storing it, confirm what the engine reads when the value is blank.",
    faq: [
      {
        q: "What is the difference between wait and drip?",
        a: "A wait holds one contact for a period. Drip mode holds a queue of contacts and releases them in batches — a throughput control rather than a per-contact delay.",
      },
      {
        q: "Do waits respect business hours?",
        a: "They can. A window on the wait resumes it only inside the days and hours you choose, and a send window on the automation additionally queues messages to the next open slot.",
      },
    ],
    templates: ["appointment-reminders", "abandoned-cart-recovery", "trade-show-lead-followup"],
    related: ["send-window", "drip-campaign", "goal", "no-show"],
  },
  {
    slug: "if-else-branch",
    term: "If/Else branch",
    aka: ["condition step", "decision node", "conditional split"],
    category: "Automation mechanics",
    definition:
      "An If/Else branch evaluates a set of rules about the contact at the moment the run reaches it and sends them down one of two paths, letting a single automation behave differently for different people.",
    body: [
      "A branch is a question asked at a specific instant. That instant matters more than the rule: a contact who will buy tomorrow evaluates as not-a-customer today, and the branch is right to send them down the nurture path.",
      "The failure mode is not wrong rules, it is orphaned paths. A branch creates two futures, and it is easy to build the interesting one carefully and leave the other pointing nowhere. Every branch deserves the question: what does someone on the boring side actually receive?",
      "Branches also tempt over-segmentation. Three nested branches produce eight paths, of which you will maintain two. Prefer a shallow branch on something that genuinely changes the message.",
    ],
    inFrazzle: [
      "Rules match on all or any, and can read email, phone, name, tags, custom fields, incoming webhook or API payload values, and per-channel do-not-disturb state.",
      "Rules also resolve CRM state at evaluation time: whether the contact is active in another flow, whether they have clicked a given automation's trigger link, who owns them, whether they have an upcoming appointment, and how they are related to other contacts.",
      "Each side targets the next step, a specific step, or — as a deliberate construct — an End node.",
      "Branches merge back to the join by default; the only way a branch stops is an explicit End step placed inside it.",
    ],
    gotcha:
      "Leaving the else path empty on the assumption that it means stop is how contacts get stranded one step short of the thing you wanted them to do. Branches fall through to the join unless you place an End node deliberately.",
    faq: [
      {
        q: "What is the difference between an If/Else branch and a split test?",
        a: "A branch decides using facts about the contact and is reproducible. A split test decides at random by weight, so the same contact could have gone either way.",
      },
      {
        q: "Can a branch check what happened in another automation?",
        a: "Yes — rules can test whether a contact is active, completed, exited or failed in a named flow, and whether they clicked a specific automation's trigger link.",
      },
    ],
    templates: ["quiz-funnel-segmentation", "trial-to-demo-sales-assist", "review-referral-engine"],
    related: ["tag", "custom-field", "split-test", "goal"],
  },
  {
    slug: "goal",
    term: "Goal",
    aka: ["goal step", "conversion goal", "jump-to goal"],
    category: "Automation mechanics",
    definition:
      "A goal is a step that represents the outcome an automation is trying to produce. Contacts anywhere upstream jump straight to it the moment the goal's event happens, skipping whatever remained in between.",
    body: [
      "Goals invert how a sequence is normally read. Instead of a linear path with an exit bolted on, the sequence becomes a set of attempts at a single outcome, and the outcome watches for itself.",
      "This is what stops the most embarrassing automation failure: continuing to ask somebody to book a call for another eight days after they booked the call. The goal catches them at whatever step they had reached and moves them on.",
      "Because a goal is a real step and not just a setting, it is visible. Anyone reading the map can see what the sequence is for, which is more than can be said for most workflow diagrams.",
    ],
    inFrazzle: [
      "A goal watches an event: a tag added or removed, a trigger link clicked, an inbound message from the contact, an email opened or clicked, or a condition becoming true.",
      "On arrival it can continue into the steps after the goal, wait there, or end the run.",
      "Goals and stop tags solve adjacent problems: a goal is the success exit, a stop tag is the abort. Most sequences want both.",
      "The jump happens from wherever the contact was, including mid-wait.",
    ],
    gotcha:
      "A goal placed at the very end of a flow does nothing that ending the flow would not — its value comes from being reachable from every step above it, so put the steps that should be skippable above it, not below.",
    faq: [
      {
        q: "What is the difference between a goal and a conversion?",
        a: "A conversion is a business outcome you measure. A goal is the mechanism inside the automation that notices the outcome and changes what the contact receives next.",
      },
      {
        q: "Can one automation have several goals?",
        a: "Yes, and it is common — for example a booked goal and a replied goal in a sequence where either is a good enough reason to stop selling.",
      },
    ],
    templates: ["discovery-call-application", "webinar-funnel", "winback-reengagement"],
    related: ["stop-tag", "enrollment", "nurture-sequence", "trigger-link"],
  },
  {
    slug: "stop-tag",
    term: "Stop tag",
    aka: ["exit tag", "kill tag", "suppression tag"],
    category: "Automation mechanics",
    definition:
      "A stop tag is a tag that cancels a contact's active runs in an automation as soon as it is applied — the emergency brake that stops a sequence for someone who has unsubscribed, complained, bought, or otherwise stopped being the right audience.",
    body: [
      "Stop tags exist because the alternative is editing every sequence whenever a new reason to stop appears. One tag named unsubscribed, honoured across every automation, is a policy you can actually keep.",
      "They are also the cleanest way to give sales a lever. A rep who tags a contact as in-conversation should be able to silence the marketing sequence immediately, without filing a ticket or knowing which flows the contact is in.",
      "The discipline they require is naming. Stop tags are matched by name, so a tag applied as Unsubscribed and configured as unsubscribed is a bug waiting for its first complaint — pick one casing convention and keep it.",
    ],
    inFrazzle: [
      "Stop tags are an automation setting, listed per automation, and they cancel active runs rather than merely blocking new entries.",
      "The unsubscribe link is a system trigger link with its own protected behaviour, so honouring opt-outs does not depend on remembering to build it.",
      "The cross-automation audit flags a self-cancelling stop tag — a flow that adds a tag which is also one of its own stop tags, so the run kills itself at the next step.",
      "Because they are tags, anything that can apply a tag can stop a sequence: a step, a rep, an import, a webhook.",
    ],
    gotcha:
      "A flow that adds its own stop tag terminates every run at the next step, quietly and completely. It looks exactly like a sequence with a broken sending credential, and it is the first thing worth checking when a sequence stops mid-flight for everyone at once.",
    faq: [
      {
        q: "What is the difference between a stop tag and unsubscribing?",
        a: "Unsubscribing is the contact's choice and should suppress all marketing sending. A stop tag is your control — it can encode unsubscribes, but also purchased, in-conversation or bad-fit.",
      },
      {
        q: "Does a stop tag remove the contact from the CRM?",
        a: "No. It cancels their runs. The contact, their history and their tags all remain.",
      },
    ],
    templates: ["winback-reengagement", "abandoned-cart-recovery", "failed-payment-dunning"],
    related: ["tag", "goal", "enrollment", "do-not-disturb"],
  },
  {
    slug: "re-entry",
    term: "Re-entry",
    aka: ["reentry", "re-enrollment", "re-enrolment"],
    category: "Automation mechanics",
    definition:
      "Re-entry is the rule deciding whether a contact who has already been through an automation can enter it again — repeatedly, once ever, or only after their previous run has finished.",
    body: [
      "Re-entry is a small setting with outsized consequences, because it is the difference between a helpful reminder and a contact receiving the same email four times in an hour.",
      "The right answer follows from what the flow is. Repeating events — abandoned carts, failed payments, birthdays — need repeated entry. Milestones — welcome, onboarding, first purchase — should happen once ever, no matter how many times the trigger fires.",
      "After-exit is the setting people forget and most often want: it lets a contact come back through a re-engagement flow next quarter without letting a noisy trigger enrol them twice this afternoon.",
    ],
    inFrazzle: [
      "Re-entry is a per-automation setting with three modes: multiple, once, or after exit.",
      "Multiple is capped at ten enrollments per contact per day, so a misconfigured trigger degrades instead of flooding.",
      "It composes with stop tags: a stop tag ends the current run, and re-entry then decides whether the contact may come back.",
      "The audit surfaces duplicate triggers and tag-trigger loops, the two configurations that turn permissive re-entry into a visible problem.",
    ],
    gotcha:
      "Setting re-entry to multiple on a flow triggered by a tag that the flow itself adds is the fastest way to hit the daily cap, and the cap is the only thing standing between that mistake and your sending reputation.",
    faq: [
      {
        q: "Which re-entry setting should a welcome sequence use?",
        a: "Once. A welcome is a milestone; re-importing a contact should not welcome them again.",
      },
      {
        q: "Does re-entry restart the sequence from the beginning?",
        a: "Yes — a new enrollment is a new run and starts at the first step with fresh timers.",
      },
    ],
    templates: ["abandoned-cart-recovery", "birthday-loyalty-club", "newsletter-welcome-series"],
    related: ["enrollment", "automation-trigger", "stop-tag"],
  },
  {
    slug: "split-test",
    term: "Split test",
    aka: ["A/B test", "split path", "multivariate test"],
    category: "Automation mechanics",
    definition:
      "A split test randomly divides contacts reaching a point in an automation between two or more paths by weight, so different subject lines, offers or timings can be compared on comparable audiences.",
    body: [
      "Randomness is the whole mechanism. A split is only informative because the two groups differ in nothing except what you changed, which is exactly what makes it different from an If/Else branch.",
      "The usual mistake is scale. A split test needs enough contacts per path for the difference to mean anything, and most small-list tests conclude that a 4% difference on 60 contacts is real when it is noise. If a week of traffic will not produce a few hundred per path, the test will not answer the question.",
      "The second mistake is testing everything at once. Change the subject line or the send time, not both, or you learn only that something moved.",
    ],
    inFrazzle: [
      "A split step takes weighted paths — 50/50, 90/10, or three ways — each targeting the next step, a specific step, or an End.",
      "Because it is a step on the map, both variants are visible side by side rather than hidden behind a settings tab.",
      "Paths rejoin at the ordinary join point unless you deliberately end one, so a test does not have to duplicate the rest of the sequence.",
      "Tags applied inside a path record which variant a contact received, which is what makes the result analysable afterwards.",
    ],
    gotcha:
      "Weighting a split unevenly to be safe and then comparing raw counts rather than rates is the most common way to read a test backwards. Compare per-path rates, and write the weights down.",
    faq: [
      {
        q: "How is a split test different from a conditional branch?",
        a: "A split is random by weight; a branch is deterministic and based on the contact's data. If you can predict which side someone lands on, it is not a test.",
      },
      {
        q: "How do I know which variant a contact got?",
        a: "Tag them inside the path. Without a marker, you can see the aggregate outcome but cannot attribute it.",
      },
    ],
    templates: ["product-launch-runway", "newsletter-welcome-series", "back-in-stock-waitlist"],
    related: ["if-else-branch", "tag", "email-tracking"],
  },

  // ------------------------------------------------------- contacts & data
  {
    slug: "tag",
    term: "Tag",
    aka: ["label", "contact tag"],
    category: "Contacts & data",
    definition:
      "A tag is a simple label attached to a contact that records a fact about them — a source, an interest, a state — and which automations can react to, branch on, and use as an exit condition.",
    body: [
      "Tags earn their place by being both data and control surface. Adding one records something; adding one can also start a sequence, stop a sequence, or change which path a contact takes. That dual role is why tags become the backbone of a CRM faster than any other object.",
      "It is also why tag sprawl hurts. A workspace with 300 tags, forty of which mean roughly downloaded-something, cannot be branched on reliably by anyone who did not create them. Tags need a naming convention early, when it is still cheap.",
      "The useful discipline is to distinguish state tags from event tags. A state tag describes what is true now (customer, unsubscribed, trial) and should be removed when it stops being true. An event tag records that something happened (attended-webinar-march) and should never be removed.",
    ],
    inFrazzle: [
      "Add Tag and Remove Tag are steps; tag added and tag removed are triggers; tags are branch conditions and goal events.",
      "Stop tags are configured per automation and cancel active runs when applied.",
      "The audit maps the full relationship graph of which automations add, remove and watch which tags — the fastest way to find out what a tag actually does before you rename it.",
      "Tags are matched by name, so casing and spelling conventions are functional decisions, not cosmetic ones.",
    ],
    gotcha:
      "Using a tag both as a trigger for a flow and as something that flow applies creates a loop. It is the single most common way to accidentally send thousands of messages, and worth checking every time a tag appears in two places.",
    faq: [
      {
        q: "Tags or custom fields?",
        a: "Tags for yes/no facts you want to branch and trigger on. Custom fields for values you need to store and merge into a message — a renewal date, a plan name, a quoted price.",
      },
      {
        q: "Should I remove tags when they stop being true?",
        a: "For state tags, yes, or your branches drift out of date. For event tags recording that something happened, no — history should not be rewritten.",
      },
    ],
    templates: ["quiz-funnel-segmentation", "referral-partner-program", "trade-show-lead-followup"],
    related: ["custom-field", "stop-tag", "automation-trigger", "if-else-branch"],
  },
  {
    slug: "custom-field",
    term: "Custom field",
    aka: ["custom property", "contact attribute", "merge data"],
    category: "Contacts & data",
    definition:
      "A custom field stores a value on a contact that the standard fields do not cover — a renewal date, a plan name, a quoted price, an external system's id — for use in personalisation, branching and date-driven automation.",
    body: [
      "Fields hold values; tags hold facts. The moment you find yourself creating tags named price-under-500, price-500-1000 and price-over-1000, you wanted a field and a comparison.",
      "Date fields are the highest-leverage kind, because they turn a static record into a scheduler. A renewal date on the contact means a renewal reminder can exist without anyone maintaining a calendar.",
      "Fields are also where integration lives. Storing a partner system's identifier on the contact is what lets a later webhook find the right person, which matters more than it sounds when the incoming payload has no email address.",
    ],
    inFrazzle: [
      "An Update Field step can set, clear, copy, add, subtract, multiply, divide, or shift a date forward or back by days, weeks or months.",
      "Field changed is a trigger, and a date field can drive a trigger or a wait with an offset before or after, optionally repeating annually.",
      "Fields are readable in branch rules with comparison operators, and merge into email and SMS content.",
      "Contact lookup can match on a custom field, including a contains match against a field holding many appended values — the pattern for storing an unbounded history of external ids on one contact.",
    ],
    gotcha:
      "Date fields have to hold a real date in a consistent format for date triggers and waits to see them. A date arriving from a webhook as free text looks fine on the contact record and silently never fires anything.",
    faq: [
      {
        q: "Can automations do arithmetic on a field?",
        a: "Yes — add, subtract, multiply and divide are field operations, which is how counters and simple scores are built.",
      },
      {
        q: "What happens if a merge field is empty?",
        a: "It renders as nothing, which is how emails end up greeting Hi , — guard personalised openings with a fallback or a branch.",
      },
    ],
    templates: ["real-estate-buyer-nurture", "agency-retention-reporting", "estimate-to-job-won"],
    related: ["merge-field", "tag", "lead-scoring", "inbound-webhook"],
  },
  {
    slug: "merge-field",
    term: "Merge field",
    aka: ["personalization token", "merge tag", "variable"],
    category: "Email & SMS delivery",
    definition:
      "A merge field is a placeholder in message content that is replaced with that contact's data at send time — {{name}} becoming Priya — so one written message can address thousands of people individually.",
    body: [
      "Merge fields are the oldest trick in email and still the easiest to get wrong, because they fail silently. A missing value does not raise an error; it renders as nothing, and the message goes out reading Hi , which is worse than not personalising at all.",
      "The genuinely valuable merges are rarely names. Referencing the thing someone actually did — the quote amount, the appointment time, the plan they are on — carries information, and information is what earns a reply.",
      "Data arriving from another system deserves particular care, because it is not yours and can change shape. A payload field that was a string last month can arrive as a number this month, and a message built on it will not notice.",
    ],
    inFrazzle: [
      "Subjects, bodies, preheaders and from-names accept merge fields for name, email, phone, contact id and any custom field.",
      "Values from an inbound webhook are available to the run, so a message can quote an order number or an appointment time that never had to be stored on the contact first.",
      "Appointment details merge into reminder content, which is what lets one reminder flow serve every appointment type.",
      "Trigger link destinations accept merges too, so each contact can be sent to their own URL — a per-contact magic link held in a field, for instance.",
    ],
    gotcha:
      "Personalising a subject line with a field that is empty for a third of the list is a measurable loss, not a neutral one — those subject lines arrive visibly broken. Branch on whether the field exists, or write a version that does not need it.",
    faq: [
      {
        q: "What happens if the merge field has no value?",
        a: "It renders as empty. Nothing errors, nothing is skipped, and the gap ships — which is why fallbacks and existence checks matter.",
      },
      {
        q: "Can I merge data from a webhook without saving it to the contact?",
        a: "Yes. Values from the incoming payload are available to that run, so one-off details can be quoted without permanently storing them.",
      },
    ],
    templates: ["appointment-reminders", "post-purchase-review-request", "workshop-ticket-sales"],
    related: ["custom-field", "inbound-webhook", "trigger-link"],
  },
  {
    slug: "inbound-webhook",
    term: "Inbound webhook",
    aka: ["incoming webhook", "webhook URL", "webhook trigger"],
    category: "Contacts & data",
    definition:
      "An inbound webhook is a URL your CRM exposes so that other systems can push events into it — a payment succeeded, a booking made, a form submitted — enrolling a contact and carrying the event's data into the automation.",
    body: [
      "Webhooks are how a CRM integrates with software that has no official integration. Any tool that can send an HTTP POST can start a sequence, which in practice means nearly all of them.",
      "The design question is what the payload identifies. A webhook that carries an email address can find or create the right contact easily. One that carries only a partner's internal id needs that id to have been stored on the contact earlier, which is a decision you have to make before you need it, not after.",
      "Treat the endpoint as public. The URL is unguessable rather than authenticated, so it should be handled like a secret, and the flow behind it should be built to tolerate duplicate deliveries — retries are normal, not exceptional.",
    ],
    inFrazzle: [
      "Webhook entry is deliberately not a trigger type: each automation can have any number of wired Inbound Webhook URLs, and creating one on the map mints its URL immediately.",
      "Every field of the posted JSON is available as a merge value in emails, SMS and branch rules for that run.",
      "A Find Contact step resolves who the event is about — by contact id, then by named field rules, then email, then phone — and rebinds the run to them without ever creating anyone.",
      "Map membership is the source of truth for which automation a webhook belongs to, and a recurring boot heal re-enforces that link.",
    ],
    gotcha:
      "Source systems retry. A webhook flow that has no idempotency — no once-only re-entry, no dedupe on an event id — will happily process the same payment twice and send two receipts.",
    faq: [
      {
        q: "Do I need a Zapier account to integrate?",
        a: "Not for anything that can send a webhook natively — Stripe, Shopify, Cal.com and most modern SaaS can post directly to the URL.",
      },
      {
        q: "Can a webhook create a contact that does not exist yet?",
        a: "Yes, with a create step that dedupes on email or phone. Lookup and creation are deliberately separate so an integration can choose to only ever match existing people.",
      },
    ],
    templates: ["failed-payment-dunning", "post-purchase-review-request", "workshop-ticket-sales"],
    related: ["merge-field", "automation-trigger", "custom-field", "trigger-link"],
  },
  {
    slug: "lead-scoring",
    term: "Lead scoring",
    aka: ["contact scoring", "engagement score", "MQL scoring"],
    category: "Contacts & data",
    definition:
      "Lead scoring assigns a numeric value to each contact based on their attributes and behaviour, so that sales attention can be pointed at the contacts most likely to buy rather than at the most recent ones.",
    body: [
      "Scoring is a ranking tool, not a truth. Its only job is to order a list better than recency does, and that is a low enough bar that a crude score usually beats no score — five points for a pricing-page visit, ten for a demo request, minus twenty for ninety days of silence.",
      "Where it goes wrong is complexity. Twenty-factor models that nobody can explain get ignored by the sales team they were built for, and a score nobody trusts is worse than none because it launders a guess as a number.",
      "It also decays. A score that only ever increases eventually says everyone is hot; subtracting for inactivity is what keeps the ranking meaningful in month six.",
    ],
    inFrazzle: [
      "There is no built-in score object — scoring is assembled from primitives, which keeps the model yours and inspectable.",
      "Hold the score in a custom field and change it with Update Field arithmetic: add on an email click, add on a trigger link click, subtract on a schedule.",
      "Branch on the value with greater-than and less-than rules, and use a threshold crossing to notify an owner or enrol the contact in a sales flow.",
      "Because each change is a visible step on the map, the model is readable by whoever inherits it.",
    ],
    gotcha:
      "A score that only goes up is a clock, not a score. Without a decay path — a scheduled subtraction, or a reset on inactivity — everything drifts to the top of the list and the ranking stops meaning anything.",
    faq: [
      {
        q: "Does FrazzleCRM have built-in lead scoring?",
        a: "Not as a dedicated feature. It is built from a custom field plus field arithmetic and branch rules, which is more work to set up and considerably easier to audit.",
      },
      {
        q: "What is a reasonable starting model?",
        a: "Three or four behaviours with round numbers, one decay rule, and one threshold that does something visible. Refine only once the sales team disagrees with it for a specific reason.",
      },
    ],
    templates: ["trial-to-demo-sales-assist", "discovery-call-application", "agency-retention-reporting"],
    related: ["custom-field", "if-else-branch", "email-tracking", "tag"],
  },

  // -------------------------------------------------- email & sms delivery
  {
    slug: "trigger-link",
    term: "Trigger link",
    aka: ["tracked link", "action link", "smart link"],
    category: "Email & SMS delivery",
    definition:
      "A trigger link is a tracked URL that records who clicked it and can start or advance an automation before forwarding the visitor to the real destination — turning a click into an event without needing a form.",
    body: [
      "Trigger links are the lightest possible conversion mechanism. Asking somebody to click yes, I am still interested costs them nothing, and it produces a real, attributable signal you can branch on.",
      "They are also the honest way to run a preference or opt-down flow. Send fewer emails, pause for a month and unsubscribe can each be a link, which routinely retains people who would otherwise have left entirely.",
      "The important property is that a click identifies a specific known contact rather than an anonymous visitor, which is what makes it usable as an automation event at all.",
    ],
    inFrazzle: [
      "A trigger link is its own object with a name and a destination; the tracked URL carries the clicker's identity so the click is attributable.",
      "Trigger link clicked is a trigger, a goal event and a branch condition — a contact having clicked a given automation's link is directly testable in rules.",
      "Destinations accept merge fields, so each contact can be forwarded to their own URL — a personal magic link held in a custom field, for example.",
      "Unsubscribe is a protected system trigger link, and links are searchable by name, destination and URL from both the map and the automations list.",
    ],
    gotcha:
      "A trigger link is a tracked redirect, not an entry point: on its own it enrols nobody. Something still has to act on the click — a trigger, a goal or a branch — or you have built a very well-instrumented link that does nothing.",
    faq: [
      {
        q: "How is a trigger link different from a UTM parameter?",
        a: "A UTM labels traffic for reporting. A trigger link identifies a specific known contact and can change what happens to them next.",
      },
      {
        q: "Do trigger links hurt deliverability?",
        a: "Redirects through an unfamiliar domain get scrutinised, so use them for links that need to do something and leave ordinary links alone. Broken destinations are the bigger risk, which is why they are worth monitoring.",
      },
    ],
    templates: ["winback-reengagement", "webinar-funnel", "referral-partner-program"],
    related: ["email-tracking", "goal", "merge-field", "tag"],
  },
  {
    slug: "send-window",
    term: "Send window",
    aka: ["quiet hours", "sending window", "business hours sending"],
    category: "Email & SMS delivery",
    definition:
      "A send window restricts the days and hours during which an automation may send, holding messages that come due outside it until the next open slot — so a sequence started at midnight does not text anybody at midnight.",
    body: [
      "Any automation with waits in it will eventually try to send at an awful time. Somebody enrols at 23:40, the wait is twenty minutes, and the tool does exactly what it was told.",
      "For SMS this is not merely rude. Many jurisdictions regulate messaging hours, and a 3am text is the kind of complaint that costs a sending number rather than a subscriber.",
      "Timezone choice is the part worth deciding deliberately. A window in your company's timezone is predictable for your team; a window in the contact's timezone is correct for the contact. International lists want the latter, and want a stored timezone to make it possible.",
    ],
    inFrazzle: [
      "The send window is a per-automation setting: days of the week, a start and end time, and whether it is evaluated in the company timezone or the contact's.",
      "Email and SMS that come due outside the window are queued to the next open slot rather than dropped.",
      "Internal notifications to teammates deliberately ignore the window — an alert about a hot lead is not a marketing message.",
      "Individual duration waits can additionally carry their own window, so one step can be constrained without constraining the whole flow.",
    ],
    gotcha:
      "A narrow window plus a large batch produces a queue, and the queue drains into the next opening all at once. Widen the window or slow the entry with drip mode; do not discover the interaction on a Monday morning.",
    faq: [
      {
        q: "Does a send window delay the whole automation?",
        a: "It delays sending. The run continues to move through non-sending steps; messages that fall outside the window wait for the next open slot.",
      },
      {
        q: "Company timezone or contact timezone?",
        a: "Contact timezone if you have reliable timezone data and an international list. Company timezone if you do not, because a wrong contact timezone is worse than a consistent company one.",
      },
    ],
    templates: ["speed-to-lead-callback", "appointment-reminders", "newsletter-welcome-series"],
    related: ["wait-step", "drip-campaign", "do-not-disturb", "a2p-10dlc"],
  },
  {
    slug: "do-not-disturb",
    term: "Do Not Disturb (DND)",
    aka: ["DND", "channel mute", "communication preferences"],
    category: "Email & SMS delivery",
    definition:
      "Do Not Disturb is a per-contact, per-channel mute that suppresses outbound email, SMS or calls to that contact without deleting them or removing them from your CRM.",
    body: [
      "DND separates two things that are usually conflated: a contact's willingness to hear from you, and their presence in your database. Someone who does not want texts may still want the invoice emails, and deleting them to stop the texts loses the relationship entirely.",
      "Per-channel granularity is what makes it usable. Blanket suppression is the blunt instrument people reach for because their tool offers nothing else, and it costs revenue every time.",
      "The essential property is that it is checkable. Suppression that silently drops messages is hard to trust; a state you can branch on lets a flow route around a muted channel rather than firing into the void.",
    ],
    inFrazzle: [
      "A Do Not Disturb step mutes or unmutes a channel — email, SMS, calls, or all of them — for the contact.",
      "DND state per channel is readable in branch rules, so a flow can check before it sends and take the other route.",
      "It composes with stop tags: DND silences a channel, a stop tag ends the run entirely.",
      "Because muting is a step, an automation can offer a pause-my-texts option via a trigger link and honour it without a human in the loop.",
    ],
    gotcha:
      "Muting a channel without branching around it means the contact continues advancing through steps that send nothing. The sequence completes, the reporting looks normal, and they received none of it.",
    faq: [
      {
        q: "Is DND the same as unsubscribing?",
        a: "No. Unsubscribing is a legal opt-out of marketing and should be honoured everywhere. DND is an operational mute you control, and can be per-channel and temporary.",
      },
      {
        q: "Can a contact set their own DND?",
        a: "Indirectly — a trigger link click can run a flow that sets it, which is how a pause my texts link works.",
      },
    ],
    templates: ["appointment-reminders", "churn-save-cancellation", "review-referral-engine"],
    related: ["stop-tag", "send-window", "trigger-link", "a2p-10dlc"],
  },
  {
    slug: "email-tracking",
    term: "Email open and click tracking",
    aka: ["open rate", "click tracking", "email engagement"],
    category: "Email & SMS delivery",
    definition:
      "Email tracking records whether a recipient opened a message or clicked a link in it — opens via a tiny embedded image, clicks via a redirect — producing the engagement data behind open rates, click rates and behaviour-triggered follow-up.",
    body: [
      "Open tracking has been unreliable since privacy-protecting mail clients began pre-fetching images on the recipient's behalf. Those pre-fetches register as opens, which inflates open rate and makes it useless as a precise measure.",
      "It remains useful as a relative signal. Comparing this campaign's opens to last campaign's, on the same list, still tells you something — just never treat the absolute number as the share of humans who read it.",
      "Clicks are much stronger evidence, because a click requires an intentional action. Any behaviour-triggered automation worth building should key on clicks rather than opens.",
    ],
    inFrazzle: [
      "Tracking is a company-level setting and is off unless enabled — no tracking pixels are injected into your sending without that switch.",
      "With it on, email opened and email clicked become triggers, and clicks can be narrowed to destinations containing given text.",
      "Both are also goal events, so a sequence can jump a contact forward the moment they engage.",
      "The first open and the first click are what fire; a flow does not re-trigger on every subsequent open of the same message.",
    ],
    gotcha:
      "Building a re-engagement flow on has not opened in 90 days marks engaged people as dormant, because their mail client may never load images. Base dormancy on clicks and replies, and let opens be a tiebreaker at most.",
    faq: [
      {
        q: "Why is my open rate suspiciously high?",
        a: "Privacy-protecting mail clients pre-fetch images, registering opens no human performed. It affects some audiences far more than others, so compare like with like over time rather than against a benchmark.",
      },
      {
        q: "Can I trigger an automation on a click to one specific link?",
        a: "Yes — a click trigger can be scoped to destinations containing given text, which is how one email can route readers by which offer they clicked.",
      },
    ],
    templates: ["product-launch-runway", "trial-to-demo-sales-assist", "winback-reengagement"],
    related: ["trigger-link", "goal", "lead-scoring", "spf-dkim-dmarc"],
  },
  {
    slug: "spf-dkim-dmarc",
    term: "SPF, DKIM and DMARC",
    aka: ["email authentication", "domain authentication", "email DNS records"],
    category: "Email & SMS delivery",
    definition:
      "SPF, DKIM and DMARC are DNS records that prove your email really comes from your domain: SPF lists who may send for you, DKIM cryptographically signs each message, and DMARC tells receivers what to do when a message fails those checks — and publishes reports on who is trying.",
    body: [
      "These three records decide whether your automation reaches the inbox before a single word of your copy matters. Missing or wrong, they are the reason a perfectly good sequence lands in spam and everyone blames the subject line.",
      "The stakes rose in 2024, when the largest mailbox providers began requiring authentication — including a DMARC policy — from anyone sending in bulk. What was once a best practice is now an entry requirement.",
      "Set-up is a one-off job of publishing records your sending provider gives you and waiting for verification. The ongoing job is remembering to redo it whenever you add a sending service, because each new sender needs to be authorised too.",
    ],
    inFrazzle: [
      "You bring your own sending credentials — Mailgun, Amazon SES, SendGrid, Postmark, Brevo or any SMTP host — so authentication is configured once at that provider, against your own domain.",
      "Every provider setup guide points at SPF and DKIM first, because that is what actually decides deliverability.",
      "There is no per-email markup on any plan; you pay your sending provider directly, which also means your domain reputation is genuinely yours.",
      "A broken-link watchdog re-checks the links in your automation emails so a dead destination is caught before subscribers meet it.",
    ],
    gotcha:
      "Publishing a strict DMARC policy before your sending sources are all authenticated tells receivers to reject your own mail. Start at p=none, read the reports until every legitimate sender passes, then tighten.",
    faq: [
      {
        q: "Do I need all three?",
        a: "Effectively yes for bulk sending. SPF and DKIM are the mechanisms; DMARC is the policy that makes them enforceable and gives you visibility.",
      },
      {
        q: "Can I send from a free Gmail address?",
        a: "Not for automated marketing volume. Sending as a large provider's address from third-party infrastructure fails alignment under their own DMARC policies. Use a domain you control.",
      },
    ],
    templates: ["newsletter-welcome-series", "lead-magnet-nurture", "product-launch-runway"],
    related: ["email-tracking", "drip-campaign", "merge-field"],
  },
  {
    slug: "a2p-10dlc",
    term: "A2P 10DLC",
    aka: ["10DLC", "10DLC registration", "A2P messaging"],
    category: "Email & SMS delivery",
    definition:
      "A2P 10DLC is the US carrier framework for application-to-person messaging sent from ordinary ten-digit long code numbers. Senders register their business and campaign with the carriers, and unregistered traffic is throttled or filtered rather than delivered.",
    body: [
      "This is a compliance step that behaves like an infrastructure step. Skipping it does not produce an error, it produces silence: messages accepted by your provider and quietly dropped downstream, which is a considerably worse failure mode than a rejection.",
      "Registration involves declaring the business and the campaign — the use case, sample message content, and how contacts opted in. Vague answers get rejected, so it goes faster when the funnel genuinely has an opt-in you can describe.",
      "Throughput follows registration. Registered campaigns get message-per-second limits based on trust, which is the practical difference between a reminder blast that lands in minutes and one that trickles out for an hour.",
    ],
    inFrazzle: [
      "SMS uses your own Twilio credentials, so registration happens in your Twilio console against your own brand.",
      "The Twilio setup guide covers buying an SMS-capable number and registering for 10DLC before US sending.",
      "No per-message markup is added on any plan — you pay Twilio directly at their rates.",
      "Send windows and Do Not Disturb are separate from carrier rules and cover the courtesy and quiet-hours side that registration does not.",
    ],
    gotcha:
      "Unregistered US long-code traffic gets filtered without an error surfacing anywhere in your automation. If SMS steps report as sent and nobody receives them, check registration status before debugging the flow.",
    faq: [
      {
        q: "Does 10DLC apply outside the United States?",
        a: "No — it is a US carrier framework. Other countries have their own rules, including sender-id registration in some markets and outright bans on unsolicited messaging in others.",
      },
      {
        q: "Do I need it for a handful of appointment reminders?",
        a: "For US long-code sending, effectively yes. Filtering does not distinguish between low and high volume, and appointment reminders are exactly the messages you cannot afford to have silently dropped.",
      },
    ],
    templates: ["appointment-reminders", "speed-to-lead-callback", "estimate-to-job-won"],
    related: ["send-window", "do-not-disturb", "no-show"],
  },
  {
    slug: "sales-script",
    term: "Sales script",
    aka: ["call script", "phone script", "discovery call script", "cold call script"],
    category: "Funnel strategy",
    definition:
      "A sales script is a planned structure for a sales conversation — the questions to ask, the points to make, and where to go next depending on how the prospect answers — written down so every rep runs the same call and you can tell which version of it actually closes.",
    body: [
      "The word script makes people picture a telemarketer reading a paragraph aloud, which is why good salespeople resist them. What earns its keep is not the wording but the structure: the order the questions come in, the point at which price is raised, and the branch you take when somebody says the number is too high. Those decisions get made under time pressure on every call, and left unwritten they get made differently every time.",
      "A structure is a tree, not a list. \"What made you book this call?\" has three or four common answers, and each one deserves a different next move. Written as a linear document, that tree collapses into a wall of prose with parenthetical asides, and the rep scrolls it while the prospect waits. Written as a flow chart, each answer is a branch you take, and the rep only ever looks at the two or three cards that come next.",
      "The reason to write it down at all is comparison. If the call is improvised, a good month and a bad month are indistinguishable — you have no record of what was said. If the path through a script is recorded, you can see that the calls which closed spent time on the budget question early, and the ones that stalled skipped it. That is a claim you can test.",
    ],
    inFrazzle: [
      "A Sales Script is a container on the funnel map — the same canvas as the automations, so the call sits next to the sequences that feed it and follow it.",
      "Five card types: Question (what the rep asks), Statement (what the rep says), Transition (how the call moves on), Action (what the rep does — share a screen, send a link) and Context (notes for the rep, never spoken).",
      "Branches are the labels on the arrows between cards: draw an edge from a question, label it \"Too expensive\", and taking that branch during a call is recorded as the route taken.",
      "Pressing Run starts a live run attached to a contact. Each card the rep clicks is appended to that run's path with a timestamp and the branch label it arrived by, and a misclick can be undone mid-call.",
      "Every rep's run is its own record — two people running the same script at the same time never collide — and the finished call appears on the contact with its full path.",
      "Ending a run fires the \"Sales script call ends\" automation trigger, so the follow-up sequence starts from the call rather than from someone remembering to tag the contact.",
      "A script is never bound to an automation: it does not consume your plan's flow allowance, and nothing inside it ever sends anything on its own.",
    ],
    gotcha:
      "Scripts get abandoned when they are written to be read aloud. Write each card as the shortest thing that reminds a rep what to do — one question, one claim, one action — and put the paragraphs of background in Context cards, which are explicitly not spoken. A card the rep has to read in full during a live call is a card they will start ignoring by the third call.",
    faq: [
      {
        q: "Does a sales script make reps sound robotic?",
        a: "It does when the script is prose to be read. A branching script does the opposite: because the next move is already decided, the rep can stop planning the call in their head and listen to the answer instead.",
      },
      {
        q: "How do you tell whether a sales script is working?",
        a: "Record the route each call actually took, then compare the routes of the calls that closed with the ones that did not. Aggregate close rate tells you the script changed something; the paths tell you which part.",
      },
    ],
    deepDive: {
      href: "/sales-scripts",
      label: "See Sales Scripts in FrazzleCRM",
      blurb:
        "The full feature page: the five card types, how branches are recorded during a live call, and what the run history does to the contact record afterwards.",
    },
    templates: [
      "discovery-call-application",
      "speed-to-lead-callback",
      "trial-to-demo-sales-assist",
    ],
    related: ["no-show", "if-else-branch", "automation-trigger", "goal"],
  },
];

export const CATEGORY_ORDER: GlossaryCategory[] = [
  "Funnel strategy",
  "Automation mechanics",
  "Contacts & data",
  "Email & SMS delivery",
];

/** One-line blurb under each category heading on the hub. */
export const CATEGORY_BLURB: Record<GlossaryCategory, string> = {
  "Funnel strategy": "What you are building and why — the shapes that move revenue.",
  "Automation mechanics": "The moving parts inside a flow: entry, timing, branching, exit.",
  "Contacts & data": "How people and the facts about them are stored, matched and segmented.",
  "Email & SMS delivery": "Getting the message to arrive — and knowing what happened when it did.",
};

export function getTerm(slug: string): GlossaryTerm | null {
  return GLOSSARY.find((t) => t.slug === slug) ?? null;
}

/** Terms grouped for the hub, in CATEGORY_ORDER. Empty groups dropped. */
export function termsByCategory(): {
  category: GlossaryCategory;
  blurb: string;
  items: GlossaryTerm[];
}[] {
  return CATEGORY_ORDER.map((category) => ({
    category,
    blurb: CATEGORY_BLURB[category],
    items: GLOSSARY.filter((t) => t.category === category),
  })).filter((g) => g.items.length > 0);
}

/** The `related` list resolved to real terms, silently dropping unknowns. */
export function relatedTerms(slug: string): GlossaryTerm[] {
  const t = getTerm(slug);
  if (!t) return [];
  return t.related.map(getTerm).filter((x): x is GlossaryTerm => Boolean(x));
}

/**
 * Inverse of `templates`: which terms a given template demonstrates. Powers
 * the "terms worth knowing" strip on template pages, so the interlinking is
 * bidirectional by construction and can never point at a term that no
 * longer exists.
 */
export function termsForTemplate(templateSlug: string, limit = 4): GlossaryTerm[] {
  return GLOSSARY.filter((t) => t.templates.includes(templateSlug)).slice(0, limit);
}
